import React, { memo } from 'react';
import { ArrowLeft, ShoppingCart, Heart, Share2, Sparkles, Leaf, Award, Star } from 'lucide-react';

import { usePreviewPanel } from '../../contexts/PreviewPanelContext';

const CONTROL_PLANE_URL = 'https://agentic-commerce-control-plane.pages.dev';

// Quality tier badge configuration
const QUALITY_BADGES = {
  luxury: { label: 'Luxury', className: 'bg-amber-100 text-amber-800', icon: Award },
  premium: { label: 'Premium', className: 'bg-purple-100 text-purple-800', icon: Star },
  'mid-range': { label: 'Quality', className: 'bg-blue-100 text-blue-800', icon: null },
  budget: { label: 'Value', className: 'bg-green-100 text-green-800', icon: Leaf },
};

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return '/images/placeholder.svg';
  if (imageUrl.startsWith('/assets/')) {
    return `${CONTROL_PLANE_URL}${imageUrl}`;
  }
  return imageUrl;
};

const formatPrice = (price) => {
  if (!price) return '';
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return `£${numPrice.toFixed(2)}`;
};

/**
 * ProductDetailView - Full product details in preview panel
 */
const ProductDetailView = memo(function ProductDetailView({ onBack }) {
  const { productData, products } = usePreviewPanel();
  const product = productData || {};

  const badge = QUALITY_BADGES[product.qualityTier];
  const BadgeIcon = badge?.icon;

  return (
    <div className="flex flex-col">
      {/* Back navigation */}
      {products && products.length > 0 && (
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-sm text-penta-grey-medium hover:text-penta-black transition-colors"
        >
          <ArrowLeft size={16} />
          Back to all products
        </button>
      )}

      {/* Product image */}
      <div className="aspect-square relative bg-neutral-50">
        <img
          src={resolveImageUrl(product.image)}
          alt={product.title || 'Product'}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {badge && (
          <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${badge.className}`}>
            {BadgeIcon && <BadgeIcon size={12} />}
            {badge.label}
          </div>
        )}
      </div>

      {/* Product info */}
      <div className="p-4 space-y-4">
        {/* Brand and title */}
        <div>
          {product.brand && (
            <p className="text-xs text-penta-grey-medium uppercase tracking-wide">
              {product.brand}
            </p>
          )}
          <h3 className="text-xl font-medium text-penta-black mt-1">
            {product.title}
          </h3>
        </div>

        {/* Price */}
        {product.price && (
          <p className="text-2xl font-semibold text-penta-black">
            {formatPrice(product.price)}
          </p>
        )}

        {/* AI Explanation */}
        {product.explanation && (
          <div className="flex items-start gap-2 p-3 bg-penta-mint/10 rounded-lg">
            <Sparkles size={16} className="text-penta-mint flex-shrink-0 mt-0.5" />
            <p className="text-sm text-penta-grey-dark">
              {product.explanation}
            </p>
          </div>
        )}

        {/* Description */}
        {product.description && (
          <div>
            <h4 className="text-sm font-medium text-penta-black mb-2">Description</h4>
            <p className="text-sm text-penta-grey-dark leading-relaxed">
              {product.description}
            </p>
          </div>
        )}

        {/* Condition (for pre-loved) */}
        {product.condition && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-penta-grey-medium">Condition:</span>
            <span className="text-sm font-medium text-penta-black capitalize">
              {product.condition}
            </span>
          </div>
        )}

        {/* Sustainability */}
        {product.sustainability && (
          <div className="flex items-center gap-2 text-green-700">
            <Leaf size={16} />
            <span className="text-sm">{product.sustainability}</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-4">
          <button className="flex-1 flex items-center justify-center gap-2 bg-penta-mint text-white py-3 rounded-lg font-medium hover:bg-penta-mint/90 transition-colors">
            <ShoppingCart size={18} />
            Add to Cart
          </button>
          <button className="p-3 border border-neutral-200 rounded-lg hover:border-penta-mint transition-colors">
            <Heart size={18} className="text-penta-grey-medium" />
          </button>
          <button className="p-3 border border-neutral-200 rounded-lg hover:border-penta-mint transition-colors">
            <Share2 size={18} className="text-penta-grey-medium" />
          </button>
        </div>
      </div>
    </div>
  );
});

export default ProductDetailView;
